/* eslint-disable @typescript-eslint/no-explicit-any */
import { DataField, FormField, ListOptions } from '@/components/data/dataType';
import * as Yup from 'yup';
import { Site } from '@/components/data/dataType';
import { ColumnType } from '@/components/data/DynamicTable/ColumnType';
import { DataFieldType } from './DataFieldType';

const defineYupType = (yupType: string) => {
    switch (yupType) {
        case 'string':
        case 'select-text':
            return Yup.string();
        case 'number':
        case 'currency':
        case 'select-number':
            return Yup.number();
        case 'boolean':
            return Yup.boolean();
        case 'date':
            return Yup.date();
        case 'editor':
        case 'image':
            return Yup.object();
        case 'images':
        case 'videos':
            return Yup.array().of(Yup.object());
        default:
            return Yup.string();
    }
};

const isRequired = (shape: any, required: boolean) => {
    return required ? shape.required() : shape.notRequired();
};

export const createYupSchema = (fields: DataField[]) => {
    const shapes: any = fields.map(field => {
        isRequired(defineYupType(field.dataType), field.required);
    });

    return Yup.object().shape(shapes);
};

export const convertArrayToObject = (array: any) =>
    array.reduce(
        (obj: any, item: any) => Object.assign(obj, { [item.key]: item.value }),
        {},
    );

export const getFieldValue = (data: any, field: DataField, locale: string) => {
    switch (field.dataType) {
        case 'boolean':
            if (!data) {
                return field.defaultValue;
            }
            if (data[field.name] === null) {
                return false;
            }
            return data[field.name];

        case 'images':
        case 'videos':
        case 'number':
        case 'currency':
        case 'sort-number':
            return data && data[field.name]
                ? data[field.name]
                : field.defaultValue;
        default:
            if (data) {
                const value = field.i18n
                    ? data[field.name] && data[field.name][locale]
                    : data[field.name];
                if (value) {
                    return value;
                }
                return field.defaultValue;
            }
            return field.defaultValue;
    }
};

export const setFieldValue = (
    value: any,
    i18n: boolean,
    locale: string,
    oldValue: any,
) => (i18n ? { ...oldValue, [locale]: value } : value);

export const setFieldValues = (
    data: any,
    oldData: any,
    fields: any,
    locale: string,
) =>
    convertArrayToObject(
        fields.map((field: any) => ({
            key: field.name,
            value: setFieldValue(
                data[field.name],
                field.i18n,
                locale,
                oldData[field.name],
            ),
        })),
    );

export const getFormikType = (dataType: string) => {
    switch (dataType) {
        case 'string':
        case 'select-text':
            return 'text';
        case 'number':
        case 'currency':
        case 'select-number':
            return 'number';
        case 'email':
            return 'email';
        default:
            return 'text';
    }
};

export const getNewPosition = (data: any, positionField: string) => {
    if (data.length === 0) {
        return 1;
    }
    const positions = data.map((x: any) => x[positionField] || 1);
    return Math.max(...positions) + 1;
};

export const nextPosition = (max: any, sortFieldName: string) =>
    (max?.length > 0 ? max[0][sortFieldName] || 0 : 0) + 1;

export const sortAscBy = (data: any, sortField: string) =>
    data &&
    data.sort(
        (a: any, b: any) =>
            (a?.[sortField] || Infinity) - (b?.[sortField] || Infinity),
    );

export const groupBy = (items, key) =>
    items.reduce(
        (result, item) => ({
            ...result,
            [item[key]]: [...(result[item[key]] || []), item],
        }),
        {},
    );

export const listify = (obj, mapFn) =>
    Object.entries(obj).reduce((acc, [key, value]) => {
        acc.push(mapFn(key, value));
        return acc;
    }, []);

export const setListOptions = (
    site: Site,
    translation: { [key: string]: string },
) => {
    let listOptions: ListOptions[] = [
        { value: 'pages', label: translation.optionBasicPage },
    ];

    site.pageTemplates.map(m => {
        switch (m) {
            case 'ecwid':
                listOptions = [
                    ...listOptions,
                    {
                        value: 'ecwidCategory',
                        label: translation.optionEcwidCategoryPage,
                    },
                ];
                break;
            case 'products':
                listOptions = [
                    ...listOptions,
                    { value: 'products', label: translation.optionProductPage },
                ];
                break;
            case 'productsCategories':
                listOptions = [
                    ...listOptions,
                    {
                        value: 'productsCategories',
                        label: translation.optionCategoriesPage,
                    },
                ];
                break;
            case 'productsCategory':
                listOptions = [
                    ...listOptions,
                    {
                        value: 'productsCategory',
                        label: translation.optionCategoryPage,
                    },
                ];
                break;
            case 'productsModels':
                listOptions = [
                    ...listOptions,
                    {
                        value: 'productsModels',
                        label: translation.optionModelPage,
                    },
                ];
                break;
            case 'properties':
                listOptions = [
                    ...listOptions,
                    {
                        value: 'properties',
                        label: translation.optionRealEstatePage,
                    },
                ];
                break;
            default:
        }
    });
    return listOptions;
};

export function convertToDataFieldArray(
    entries: { id: number; responses: Record<string, any> }[],
    schemas: Record<string, string>[],
    formsFields: { name: string; dataType: DataFieldType }[],
): { id: number; dataField: DataField[] }[] {
    if (!entries || !schemas || entries.length !== schemas.length) {
        return [];
    }

    return entries.map((entry, index) => {
        const schema = schemas[index] || {};

        return {
            id: entry.id,
            dataField: Object.keys(entry.responses).map(key => {
                const value = entry.responses[key];
                const title = schema?.[key] || key;
                const field = formsFields.find(f => f.name === key);
                const dataType = field?.dataType;

                return {
                    name: key,
                    i18n: false,
                    required: false,
                    title: { fr: title, en: title },
                    dataType,
                    defaultValue: value,
                    gridClass: 'col-span-1',
                    tableVisible: true,
                };
            }),
        };
    });
}

export function generateNameTitleMap(
    fields: FormField[],
    locale: string,
): Record<string, string> {
    return fields.reduce(
        (map, field) => {
            const localizedTitle = field.title[locale];

            if (localizedTitle) {
                map[field.name] = localizedTitle;
            }

            return map;
        },
        {} as Record<string, string>,
    );
}

export const getColumns = (
    fields: DataField[] | FormField[],
    locale: string,
    filter?: string,
) => {
    return fields
        .filter(field => !filter || field[filter as keyof DataField])
        .map(field => {
            const column: ColumnType = {
                header: field.title[locale] || field.name,
                accessor: field.name,
                key: field.name,
                dataType: field.dataType as DataFieldType,
            };
            return column;
        });
};
